import { ChevronRight, Droplet, Sprout, type LucideIcon } from "lucide-react";
import { useTracker } from "@ferrivia/auth-web";
import { useMarket, useTranslation } from "@ferrivia/i18n";
import { Text } from "@ferrivia/ui";
import type { ClientTabPath } from "@ferrivia/analytics";
import { useTabNavigation } from "../../../../app/useTabNavigation";
import s from "./PathChooserBlock.module.css";

/**
 * Home's two ways in, shown until the client starts one (the server's
 * pathChooser rule). The cards sell intent, not steps — the steps live on
 * each tab — and carry no price.
 *
 * Both cards, in the same order, for every client: which path suits someone
 * is theirs to say, and the copy describes the route, never the client's
 * values. Each icon is its destination tab's own (app/routes.ts).
 */
export function PathChooserBlock() {
  const { t } = useTranslation("client");
  const { goToTab } = useTabNavigation();
  const tracker = useTracker();
  const market = useMarket();

  const choose = (path: "support" | "infusion", tab: ClientTabPath) => {
    tracker.track("home.path_chosen", { path });
    goToTab(tab, "in_app_link");
  };

  if (market === "us") return null;

  return (
    <section className={s.root} aria-labelledby="path-chooser-heading">
      <Text as="h2" size="lg" weight="bold" id="path-chooser-heading" className={s.heading}>
        {t("home.blocks.pathChooser.heading", "Miten voimme auttaa sinua?")}
      </Text>
      <div className={s.cards}>
        <PathCard
          icon={Sprout}
          title={t("home.blocks.pathChooser.support.title", "Raudanpuutteen alkukartoitus")}
          description={t(
            "home.blocks.pathChooser.support.description",
            "Ruokavalio ja elintavat ensin. Ravitsemusvalmentaja Mari tekee kanssasi suunnitelman raudan imeytymisen tueksi.",
          )}
          onClick={() => choose("support", "/support")}
        />
        <PathCard
          icon={Droplet}
          title={t("home.blocks.pathChooser.infusion.title", "Suoraan rautainfuusioon")}
          description={t(
            "home.blocks.pathChooser.infusion.description",
            "Kun matalat rautavarastot ovat vaivanneet pitkään ja haluat lääkärin arvion nyt.",
          )}
          onClick={() => choose("infusion", "/infusion")}
        />
      </div>
    </section>
  );
}

function PathCard({
  icon: Icon,
  title,
  description,
  onClick,
}: {
  icon: LucideIcon;
  title: string;
  description: string;
  onClick: () => void;
}) {
  return (
    <button type="button" className={s.card} onClick={onClick}>
      <Icon className={s.icon} aria-hidden="true" />
      <span className={s.text}>
        <span className={s.title}>{title}</span>
        <span className={s.description}>{description}</span>
      </span>
      <ChevronRight className={s.chevron} aria-hidden="true" />
    </button>
  );
}
